/**
 * Where a sign-in lands when it comes back.
 *
 * Both providers arrive here and both carry what they carry in the URL
 * FRAGMENT rather than the query string. A fragment is never sent to a server,
 * never lands in an access log and never rides a Referer header, which matters
 * because what it carries is a credential.
 *
 * It is still a credential in a URL, so the first thing this screen does after
 * reading it is wipe it out of the address bar. That leaves it in the tab's
 * history entry and nowhere else, and the entry is replaced rather than pushed
 * so Back does not walk into a spent link.
 *
 * The two providers differ only in what is in the fragment:
 *
 *   magic   the signed link token, to be redeemed here
 *   google  the finished result, already redeemed by the callback
 *
 * so magic does a round trip and google does not.
 */

import { useEffect, useRef, useState } from 'react';

import { consumeMagicLink, type SignedIn } from '../lib/api';
import { navigate } from '../lib/router';
import { useGame } from '../store/gameStore';
import { identityToken } from '../store/storage';

type Phase =
  | { readonly kind: 'working' }
  | { readonly kind: 'done'; readonly signedIn: SignedIn }
  | { readonly kind: 'failed'; readonly why: string };

const REASONS: Record<string, string> = {
  bad_link: 'That link has expired or has already been used.',
  link_used: 'That link has already been used. Ask for a fresh one.',
  no_device: 'This browser has not played yet, so there is nothing to sign in.',
  state_used: 'That sign-in was already finished. Try again.',
  bad_state: 'That sign-in could not be verified. Try again.',
  exchange_failed: 'Google did not complete the sign-in. Try again.',
  claim_refused: 'That address is already used by another account.',
  missing_code: 'The sign-in came back incomplete. Try again.',
};

function reasonFor(code: string): string {
  return REASONS[code] ?? 'That sign-in did not complete. Try again.';
}

export function AuthScreen({
  provider,
}: {
  readonly provider: 'magic' | 'google';
}): React.JSX.Element {
  const adoptIdentity = useGame((s) => s.adoptIdentity);
  const [phase, setPhase] = useState<Phase>({ kind: 'working' });
  /*
   * React runs effects twice in development, and this one redeems a
   * single-use link. Without the guard the second run spends a token the
   * first already spent and the screen reports a failure for a sign-in that
   * worked. A ref rather than state, because it must not survive into a
   * render and must not be batched.
   */
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const fragment = window.location.hash.replace(/^#/, '');
    // Wiped immediately, and REPLACED rather than pushed, so Back cannot walk
    // into a link that has already been spent.
    window.history.replaceState(null, '', window.location.pathname);

    if (fragment === '') {
      setPhase({ kind: 'failed', why: 'missing_code' });
      return;
    }

    if (provider === 'google') {
      const params = new URLSearchParams(fragment);
      const error = params.get('error');
      if (error !== null) {
        setPhase({ kind: 'failed', why: error });
        return;
      }
      const token = params.get('token');
      const playerId = params.get('playerId');
      if (token === null || playerId === null) {
        setPhase({ kind: 'failed', why: 'missing_code' });
        return;
      }
      const signedIn: SignedIn = {
        token,
        playerId,
        handle: params.get('handle') ?? '',
        email: params.get('email') ?? '',
        merged: params.get('merged') === '1',
      };
      adoptIdentity(signedIn);
      setPhase({ kind: 'done', signedIn });
      return;
    }

    // No device token is the new-install restore, not a failure: the gateway
    // mints a player for a browser that has never opened a socket.
    void consumeMagicLink(decodeURIComponent(fragment), identityToken() ?? '')
      .then((signedIn) => {
        adoptIdentity(signedIn);
        setPhase({ kind: 'done', signedIn });
      })
      .catch((err: unknown) => {
        setPhase({ kind: 'failed', why: String((err as Error).message) });
      });
  }, [provider, adoptIdentity]);

  return (
    <main className="gg-screen gg-center">
      <div className="gg-invite">
        {phase.kind === 'working' ? (
          <>
            <h1 className="gg-title">Signing you in</h1>
            <p className="gg-note">One moment.</p>
          </>
        ) : null}

        {phase.kind === 'done' ? (
          <>
            <p className="gg-subtitle">Signed in</p>
            <h1 className="gg-title">{phase.signedIn.handle}</h1>
            <p className="gg-invite-facts">{phase.signedIn.email}</p>
            <p className="gg-note">
              {phase.signedIn.merged
                ? 'Your progress on this device has been added to your account.'
                : 'Your progress is saved to this account from now on.'}
            </p>
            <button
              type="button"
              className="gg-btn gg-btn-primary gg-btn-xl mt-4"
              onClick={() => navigate({ name: 'home' }, { replace: true })}
            >
              Play
            </button>
          </>
        ) : null}

        {phase.kind === 'failed' ? (
          <>
            <p className="gg-subtitle">Sign-in</p>
            <h1 className="gg-title">That did not work</h1>
            <p className="gg-note gg-note-error">{reasonFor(phase.why)}</p>
            <button
              type="button"
              className="gg-btn gg-btn-primary mt-4"
              onClick={() => navigate({ name: 'home' }, { replace: true })}
            >
              Back to the globe
            </button>
          </>
        ) : null}
      </div>
    </main>
  );
}
